/* eslint-disable @typescript-eslint/no-deprecated */
'use client';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';

import type { ChartType, ColumnMapping } from '@/types/dashboard';

const CHART_COLORS = [
  'var(--mui-palette-primary-main)',
  'var(--mui-palette-success-main)',
  'var(--mui-palette-warning-main)',
  'var(--mui-palette-error-main)',
  'var(--mui-palette-info-main)',
  'var(--mui-palette-secondary-main)',
  'var(--mui-palette-primary-light)',
  'var(--mui-palette-success-light)',
];

interface ChartRendererProps {
  chartType: ChartType;
  data: Record<string, unknown>[];
  columnMapping: ColumnMapping;
  animationKey: number;
}

export function ChartRenderer({
  chartType,
  data,
  columnMapping,
  animationKey,
}: ChartRendererProps) {
  if (!data.length) {
    return null;
  }

  const mappingKey = JSON.stringify(columnMapping);

  return (
    <AnimatePresence mode='wait'>
      <motion.div
        key={`${animationKey}-${chartType}-${mappingKey}`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        style={{ width: '100%', height: '100%' }}
      >
        {renderChart(chartType, data, columnMapping)}
      </motion.div>
    </AnimatePresence>
  );
}

function renderChart(
  chartType: ChartType,
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  switch (chartType) {
    case 'BAR':
      return renderBarChart(data, mapping);
    case 'LINE':
      return renderLineChart(data, mapping);
    case 'AREA':
      return renderAreaChart(data, mapping);
    case 'PIE':
      return renderPieChart(data, mapping);
    case 'DONUT':
      return renderDonutChart(data, mapping);
    case 'SCATTER':
      return renderScatterChart(data, mapping);
    case 'TABLE':
      return renderTable(data);
    default:
      return null;
  }
}

function renderBarChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const xKey = mapping.xColumn;
  const yKeys = mapping.yColumns ?? [];
  if (!xKey || !yKeys.length) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <BarChart data={data}>
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--mui-palette-divider)'
        />
        <XAxis
          dataKey={xKey}
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <YAxis
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            isAnimationActive={true}
            animationDuration={600}
            animationBegin={i * 50}
          >
            {data.map((_, j) => (
              <Cell
                key={j}
                fill={
                  CHART_COLORS[(yKeys.length > 1 ? i : j) % CHART_COLORS.length]
                }
              />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function renderLineChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const xKey = mapping.xColumn;
  const yKeys = mapping.yColumns ?? [];
  if (!xKey || !yKeys.length) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <LineChart data={data}>
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--mui-palette-divider)'
        />
        <XAxis
          dataKey={xKey}
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <YAxis
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((key, i) => (
          <Line
            key={key}
            type='monotone'
            dataKey={key}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={2}
            dot={false}
            isAnimationActive={true}
            animationDuration={800}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function renderAreaChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const xKey = mapping.xColumn;
  const yKeys = mapping.yColumns ?? [];
  if (!xKey || !yKeys.length) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <AreaChart data={data}>
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--mui-palette-divider)'
        />
        <XAxis
          dataKey={xKey}
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <YAxis
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
        {yKeys.length > 1 && <Legend />}
        {yKeys.map((key, i) => (
          <Area
            key={key}
            type='monotone'
            dataKey={key}
            stroke={
              i === 0
                ? 'var(--mui-palette-info-main)'
                : CHART_COLORS[i % CHART_COLORS.length]
            }
            fill={
              i === 0
                ? 'var(--mui-palette-info-main)'
                : CHART_COLORS[i % CHART_COLORS.length]
            }
            fillOpacity={0.3}
            isAnimationActive={true}
            animationDuration={800}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function renderPieChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const labelKey = mapping.labelColumn;
  const valueKey = mapping.valueColumn;
  if (!labelKey || !valueKey) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={labelKey}
          cx='50%'
          cy='50%'
          outerRadius='80%'
          isAnimationActive={true}
          animationDuration={600}
          animationBegin={0}
          label={(props: PieLabelRenderProps) =>
            `${props.name ?? ''}: ${((props.percent ?? 0) * 100).toFixed(0)}%`
          }
        >
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

function renderDonutChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const labelKey = mapping.labelColumn;
  const valueKey = mapping.valueColumn;
  if (!labelKey || !valueKey) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <PieChart>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={labelKey}
          cx='50%'
          cy='50%'
          innerRadius='50%'
          outerRadius='80%'
          isAnimationActive={true}
          animationDuration={600}
          label={(props: PieLabelRenderProps) =>
            `${props.name ?? ''}: ${((props.percent ?? 0) * 100).toFixed(0)}%`
          }
        >
          {data.map((_, i) => (
            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

function renderScatterChart(
  data: Record<string, unknown>[],
  mapping: ColumnMapping
) {
  const xKey = mapping.xColumn;
  const yKeys = mapping.yColumns ?? [];
  if (!xKey || !yKeys.length) return null;

  return (
    <ResponsiveContainer width='100%' height='100%'>
      <ScatterChart>
        <CartesianGrid
          strokeDasharray='3 3'
          stroke='var(--mui-palette-divider)'
        />
        <XAxis
          dataKey={xKey}
          name={xKey}
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <YAxis
          dataKey={yKeys[0]}
          name={yKeys[0]}
          tick={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 12 }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--mui-palette-background-paper)',
            border: '1px solid var(--mui-palette-divider)',
          }}
        />
        <Scatter
          data={data}
          fill={CHART_COLORS[0]}
          isAnimationActive={true}
          animationDuration={600}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

function renderTable(data: Record<string, unknown>[]) {
  if (!data.length) return null;
  const columns = Object.keys(data[0]);

  return (
    <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col} sx={{ fontWeight: 600 }}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, i) => (
            <motion.tr
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02, duration: 0.2 }}
              style={{ display: 'table-row' }}
            >
              {columns.map((col) => (
                <TableCell key={col}>
                  {row[col] === null || row[col] === undefined
                    ? ''
                    : `${row[col] as string | number}`}
                </TableCell>
              ))}
            </motion.tr>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
